import { useCallback, useRef, useState } from 'react';
import { useLocalStorageState } from 'ahooks';

import { useUser, isUserLoggedIn } from '~/modules/user';
import { useAuth } from '~/modules/root/context/AuthContext';
import { useFeature } from '~/modules/root';
import { useDownloadGate } from '~/modules-rocco/product/download-gate-state';

/**
 * Where "this browser already earned Specs access from that professional" is
 * kept. Shared so the revamped gate, which grants access from its own success
 * step, writes the same key {@link useGatedDownloads} reads.
 */
export const specsAccessStorageKey = (professionalID: number): string =>
    `specsAccess_${professionalID}`;

/**
 * Records the Specs-tier lead and remembers the access it buys, for a download
 * completed outside {@link useGatedDownloads} — the gate modal resumes on its
 * own success step, by which point the hook that started the attempt may be
 * unmounted with the product-files modal that hosted it.
 */
export const grantSpecsAccess = (
    professionalID: number,
    productID: number,
): void => {
    try {
        window.localStorage.setItem(
            specsAccessStorageKey(professionalID),
            JSON.stringify(true),
        );
    } catch {
        // Storage full / blocked — the lead below still lands; the reader is
        // only asked again on a later visit.
    }
    void requestDownloadAccessMutation(professionalID, productID).catch(
        () => undefined,
    );
};

/** Sends the requestDownloadAccess mutation via plain fetch (no Apollo needed). */
export const requestDownloadAccessMutation = (
    professionalID: number,
    libraryItemID: number,
): Promise<void> =>
    fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            query: `mutation RequestDownloadAccess($ProfessionalID: Int!, $LibraryItemID: Int) {
                requestDownloadAccess(ProfessionalID: $ProfessionalID, LibraryItemID: $LibraryItemID) {
                    ... on Contact { ID }
                }
            }`,
            variables: {
                ProfessionalID: professionalID,
                LibraryItemID: libraryItemID,
            },
        }),
    }).then(() => undefined);

interface UseGatedDownloadsOptions {
    /** The product page ID. */
    productID: number;
    /** The professional's ID. */
    professionalID: number;
    /** Product hero for the gate's brand pane — the item being downloaded. */
    productImage?: string;
    /** Called before the auth modal opens (e.g. close a hosting modal). */
    onBeforeAuth?: () => void;
    /** Called when the user cancels the auth modal. */
    onAuthCancel?: () => void;
}

/**
 * Per-file download gating shared by the product-files modal and the revamp's
 * inline Downloads & Documentation accordion row:
 * - Safety: always free
 * - Specs: downloading creates a Lead (once per session, persisted per
 *   professional in localStorage) and shares the user's details
 * - ProDesign: requires sign-in
 */
export const useGatedDownloads = ({
    productID,
    professionalID,
    productImage,
    onBeforeAuth,
    onAuthCancel,
}: UseGatedDownloadsOptions) => {
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { showAuthModal, hideAuthModal } = useAuth();
    const gateEnabled = useFeature('auth_modal_2026');
    const { open: openDownloadGate } = useDownloadGate();

    const [hasSpecsAccess, setHasSpecsAccess] = useLocalStorageState<boolean>(
        specsAccessStorageKey(professionalID),
        { defaultValue: false },
    );

    const hasCreatedLead = useRef(false);
    const [leadConfirmed, setLeadConfirmed] = useState(false);

    /** Whether Specs-tier files should be gated (not logged in, or no prior access). */
    const specsGated = !isLoggedIn || !hasSpecsAccess;

    /** Creates a lead on the first gated download, then persists access for future visits. */
    const createLeadIfNeeded = useCallback(() => {
        if (hasCreatedLead.current) return;
        hasCreatedLead.current = true;
        setHasSpecsAccess(true);
        setLeadConfirmed(true);

        requestDownloadAccessMutation(professionalID, productID).catch(
            () => undefined,
        );
    }, [professionalID, productID, setHasSpecsAccess]);

    /**
     * Handles a gated file download. Shows the auth modal if needed,
     * then opens the file and creates a lead for Specs-tier files.
     */
    const handleGatedDownload = useCallback(
        (link: string, isSpecsTier: boolean) => {
            const doDownload = () => {
                window.open(link, '_blank', 'noopener');
                if (isSpecsTier) {
                    createLeadIfNeeded();
                }
            };

            if (!isLoggedIn) {
                onBeforeAuth?.();
                if (gateEnabled) {
                    // The revamped gate carries the attempt through auth and
                    // hands the file back on the far side, so the reader does
                    // not have to find their way back to it.
                    openDownloadGate({
                        action: 'download',
                        link,
                        isSpecsTier,
                        productID,
                        professionalID,
                        productImage,
                    });
                    return;
                }
                showAuthModal({
                    authSource: 'productFiles',
                    skipPostSignUp: true,
                    redirect: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                    header: 'Sign in to download',
                    subtitle:
                        'Create a free account to download product files and resources.',
                    onAuthSuccess: () => {
                        hideAuthModal();
                    },
                    onCancel: () => {
                        hideAuthModal();
                        onAuthCancel?.();
                    },
                });
                return;
            }

            doDownload();
        },
        [
            isLoggedIn,
            gateEnabled,
            openDownloadGate,
            productID,
            professionalID,
            productImage,
            showAuthModal,
            hideAuthModal,
            createLeadIfNeeded,
            onBeforeAuth,
            onAuthCancel,
        ],
    );

    return { isLoggedIn, specsGated, leadConfirmed, handleGatedDownload };
};
